import { MoreHorizontal } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { User } from "@/types"

interface UserRowProps {
  user: User
  onDelete: (user: User) => void
  onResend: (user: User) => void
  onUserSelectedForDelete: (user: User) => void
  currentUser: User
}

export function UserRow({
  user,
  onDelete,
  onResend,
  onUserSelectedForDelete,
  currentUser,
}: UserRowProps) {
  const avatarUrl = user.avatar_url
  const avatarInitials = user.avatar_initials
  const state = user.state as string | undefined
  const showDropdown =
    state === "invited" || (state === "active" && currentUser.uid !== user.uid)

  return (
    <div className="flex items-center gap-4 py-3">
      <Avatar className="h-8 w-8 overflow-hidden rounded-full">
        <AvatarImage src={avatarUrl} alt={user.name} />
        <AvatarFallback className="rounded-lg bg-neutral-200 text-black dark:bg-neutral-700 dark:text-white">
          {avatarInitials}
        </AvatarFallback>
      </Avatar>
      <div>
        <div className="text-sm font-medium">{user.name}</div>
        <div className="flex items-center gap-2">
          <div className="text-muted-foreground text-sm">{user.email}</div>
          {state === "invited" && (
            <Badge variant="outline">invite pending</Badge>
          )}
        </div>
      </div>
      {showDropdown && (
        <div className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {user.state == "invited" && (
                <>
                  <DropdownMenuItem
                    onClick={() => {
                      onDelete(user)
                    }}
                  >
                    Revoke
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      onResend(user)
                    }}
                  >
                    Resend
                  </DropdownMenuItem>
                </>
              )}
              {user.state == "active" && currentUser.uid !== user.uid && (
                <>
                  <DropdownMenuItem
                    onClick={() => {
                      onUserSelectedForDelete(user)
                    }}
                  >
                    Remove
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
}
